import React from 'react';
import { Autocomplete, Box, Checkbox, Chip, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { CustomTooltip } from 'app/components/Common/Tooltips';

export interface IAutoCompleteOption {
  value: string;
  name: string;
  hideDeleteIcon?: boolean;
  tooltip?: string;
}

const useAutoCompleteStyles = makeStyles()(theme => ({
  chip: {
    background: theme.palette.components.chips.primary.color,
    border: `1px solid ${theme.palette.system.lightGrey}`,
    margin: '4px',
    '& .MuiChip-deleteIcon': {
      color: theme.palette.framework.system.doveGray,
    },
    '&:hover .MuiChip-deleteIcon': {
      color: theme.palette.framework.system.doveGray,
    },
  },
  autocompleteInput: {
    maxHeight: '159px',
    overflowY: 'scroll',
    '& .MuiInputBase-input': {
      color: theme.palette.system.grayChip,
    },
    '& input': {
      caretColor: 'transparent',
    },
  },
}));

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AutoCompleteRenderChips = ({
  label,
  options,
  id,
  name,
  disableClearable,
  disabledValue,
  getOptionDisabled,
  selectedValues,
  onValueChange,
  required,
  defaultOptionsToPersist,
}: {
  label: string;
  options: IAutoCompleteOption[];
  id: string;
  name: string;
  disableClearable?: boolean;
  disabledValue?: any;
  getOptionDisabled?: (option: IAutoCompleteOption) => boolean;
  selectedValues?: IAutoCompleteOption[];
  onValueChange: (data: IAutoCompleteOption[]) => void;
  required?: boolean;
  defaultOptionsToPersist?: string[];
}) => {
  const { classes } = useAutoCompleteStyles();
  const { control } = useFormContext();

  const onKeyDownCapture = event => {
    if (event.keyCode === 8) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <Controller
      defaultValue={selectedValues ?? []}
      control={control}
      name={name}
      rules={{ required: required }}
      render={({ field }) => (
        <Autocomplete
          {...field}
          multiple
          id={id}
          fullWidth
          options={options}
          disableCloseOnSelect
          disableClearable={disableClearable}
          onKeyDownCapture={onKeyDownCapture}
          onKeyPress={e => e.preventDefault()}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={option => option?.name}
          getOptionDisabled={getOptionDisabled}
          onChange={(e, data) => {
            let values = [...data] as IAutoCompleteOption[];
            if (values.length === 0) {
              var filteredArray = selectedValues.filter(
                a => defaultOptionsToPersist?.includes(a.value) || !options.some(item => item.value === a.value),
              );
              if (filteredArray?.length) {
                values = [...filteredArray];
              }
            }
            field.onChange(values);
            onValueChange(values);
          }}
          value={selectedValues ?? []}
          popupIcon={false ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          renderOption={(prop, option, { selected }) => (
            <CustomTooltip isStandardContent tooltipContent={option.tooltip ?? ''}>
              <li {...prop}>
                <Checkbox
                  sx={{
                    '& .MuiSvgIcon-root': {
                      color: theme => (prop['aria-disabled'] ? theme.palette.system.buttonGray : 'inherit'),
                    },
                  }}
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected || selectedValues?.findIndex(a => a.value === option.value) > -1}
                />
                {option?.name}
              </li>
            </CustomTooltip>
          )}
          renderInput={params => (
            <TextField {...params} label={label} variant="filled" className={classes.autocompleteInput} />
          )}
          renderTags={(values, getTagProps) => (
            <Box sx={{ p: '6px 2px' }}>
              {values
                .filter(a => options.map(c => c.value).includes(a.value))
                ?.map((option, index) => (
                  <Chip
                    key={index}
                    label={option.name}
                    {...getTagProps({ index })}
                    disabled={disabledValue && disabledValue.includes(option.name)}
                    className={classes.chip}
                    deleteIcon={!option.hideDeleteIcon ? <CloseIcon sx={{ width: '15px', height: '15px' }} /> : <></>}
                    onDelete={() => {
                      const newValue = [...selectedValues];
                      newValue.splice(
                        newValue.findIndex(a => a.value === option.value),
                        1,
                      );
                      onValueChange([...newValue]);
                    }}
                  />
                ))}
            </Box>
          )}
        />
      )}
    />
  );
};

export default AutoCompleteRenderChips;
